/*
 * Copyright (c) 2023 Bluemate B.V.
 */
import './style/editInvoice.scss';
import {ChangeFunction, ChangeKeyFunction, Loader} from '@dvrd/dvr-controls';

import React, {FocusEventHandler, MouseEventHandler, useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import Invoice from '../../../models/invoiceModel';

import OrderItem from '../../../models/orderItemModel';

import {getCountryCode, getVatItemsByCountry} from '../../../util/countryUtil';

import {AccessKey, OrderItemErrors, VATType, VATValue} from '../../../util/interfaces';

import {DefaultVAT, translateVatValue, VATValuesMatch} from '../../../util/vatUtil';

import {BluemateButton, BluemateInput, BluemateListCard, BluemateNumberInput} from '../../controls';

import PortalSelectController from '../../controls/portalSelect/portalSelectController';

import useRight from '../../misc/useRight';

import InvoiceData from '../data/invoiceData';

interface Props {
    onChangeInvoice: ChangeKeyFunction;
    onChangeItem: (item: OrderItem, key: string) => ChangeFunction;
    onSubmit: MouseEventHandler;
    onCancel: MouseEventHandler;
    onResetItemError: (id: string, key: keyof OrderItemErrors) => FocusEventHandler;
    onChangeVAT: (item: OrderItem) => ChangeFunction<VATValue>;
    invoice: Invoice;
    loading: boolean;
    itemErrors: Record<string, OrderItemErrors>;
}

export default function EditInvoice(props: Props) {
    const {t} = useTranslation();
    const [canEditItems] = useRight(AccessKey.INVOICE_EDIT_ITEMS);
    const {
        invoice,
        onChangeInvoice,
        loading,
        itemErrors,
        onResetItemError,
        onChangeItem,
        onSubmit,
        onCancel,
        onChangeVAT
    } = props;

    function getVatItems(countryCode: string): Array<VATValue> {
        const items: Array<VATValue> = [DefaultVAT(0)];
        return getVatItemsByCountry(countryCode, items);
    }

    function renderInvoice() {
        return <InvoiceData invoice={invoice} loading={loading} onChange={onChangeInvoice} />;
    }

    // One base list per render — every keystroke in an item field re-renders
    // the whole form, so per-row recomputation would multiply by N items.
    const baseVatItems = useMemo(() => getVatItems(getCountryCode(invoice.country)), [invoice.country]);

    function renderItems() {
        const items = invoice.items.filter((item: OrderItem) => !item.isCancelled);
        return (
            <BluemateListCard
                className='order-items-card'
                title={t('invoice.edit.invoiceLines', 'Factuurregels')}
                count={items.length}
            >
                <div className='order-items'>
                    <div className='row head'>
                        <label>{t('invoice.edit.col.ean', 'EAN')}</label>
                        <label>{t('invoice.edit.col.reference', 'REFERENTIE')}</label>
                        <label>{t('invoice.edit.col.name', 'NAAM')}</label>
                        <label>{t('invoice.edit.col.price', 'PRIJS')}</label>
                        <label>{t('invoice.edit.col.quantity', 'AANTAL')}</label>
                        <label>{t('invoice.edit.col.vat', 'BTW')}</label>
                        <div />
                    </div>
                    {items.map(renderItem)}
                </div>
            </BluemateListCard>
        );
    }

    function renderItem(item: OrderItem) {
        const errors: OrderItemErrors = itemErrors[item.id];
        return (
            <div key={item.id} className='row'>
                <label className='item-ean'>{item.ean}</label>
                <BluemateInput
                    shape='rounded'
                    uniformFont
                    className='item-reference'
                    label={t('invoice.edit.itemLabel.reference', 'Referentie')}
                    value={item.reference}
                    onChange={onChangeItem(item, 'reference')}
                    disabled={!canEditItems}
                />
                <BluemateInput
                    shape='rounded'
                    uniformFont
                    className='item-title'
                    label={t('invoice.edit.itemLabel.name', 'Naam')}
                    value={item.title}
                    onChange={onChangeItem(item, 'title')}
                    error={errors.title}
                    onFocus={onResetItemError(item.id, 'title')}
                    disabled={!canEditItems}
                />
                <BluemateNumberInput
                    shape='rounded'
                    uniformFont
                    className='item-price'
                    label={t('invoice.edit.itemLabel.price', 'Prijs')}
                    value={item.piecePriceString}
                    onChange={onChangeItem(item, 'piece_price_string')}
                    error={errors.price}
                    onFocus={onResetItemError(item.id, 'price')}
                    disabled={!canEditItems}
                    asCurrency
                />
                <BluemateNumberInput
                    shape='rounded'
                    uniformFont
                    className='item-quantity'
                    label={t('invoice.edit.itemLabel.quantity', 'Aantal')}
                    value={item.quantity.toString()}
                    onChange={onChangeItem(item, 'quantity')}
                    error={errors.quantity}
                    onFocus={onResetItemError(item.id, 'quantity')}
                    disabled={!canEditItems}
                />
                {renderVatSelect(item)}
            </div>
        );
    }

    function renderVatSelect(item: OrderItem) {
        // A SPLIT value is a Record — stringify it or every split collapses
        // to the same '[object Object]' key (template vs configured split).
        const vatKey = (vat: VATValue) =>
            vat.vat_type === VATType.SPLIT
                ? `SPLIT-${JSON.stringify(vat.value ?? {})}`
                : `${vat.vat_type ?? ''}-${vat.value ?? ''}`;
        let vatItems = baseVatItems;
        if (item.vat.vat_type === VATType.SPLIT) {
            // Show the item's own configured split in place of the zeroed
            // template entry — two identical 'Samengest.' rows would appear
            // otherwise.
            vatItems = vatItems.map((vat: VATValue) => (vat.vat_type === VATType.SPLIT ? item.vat : vat));
        } else if (!vatItems.some((vat: VATValue) => VATValuesMatch(vat, item.vat))) {
            // Keep showing a legacy/foreign VAT value that is no longer part
            // of the selectable set (parity with the old dedicated VAT select).
            vatItems = [item.vat, ...vatItems];
        }
        return (
            <PortalSelectController
                onChange={(value: string | null) => {
                    const match = vatItems.find((vat: VATValue) => vatKey(vat) === value);
                    if (match) onChangeVAT(item)(match);
                }}
                value={vatKey(item.vat)}
                items={vatItems.map((vat: VATValue) => ({label: translateVatValue(vat), value: vatKey(vat)}))}
                label={t('invoice.edit.itemLabel.vat', 'BTW')}
                shape='rounded'
                uniformFont
                noShadow
                className='item-vat'
                disabled={!canEditItems}
            />
        );
    }

    return (
        <div className='edit-invoice'>
            <Loader active={loading} />
            <div className='footer-container'>
                <BluemateButton
                    onClick={onCancel}
                    label={t('invoice.details.back', 'Terug')}
                    tertiary
                    disabled={loading}
                />
                <BluemateButton onClick={onSubmit} label={t('invoice.edit.save', 'Opslaan')} disabled={loading} />
            </div>
            {renderInvoice()}
            {renderItems()}
        </div>
    );
}
